import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/authService';
import { Button } from '@/components/shadcn/button';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { getNativePlatform } from '@/utils/capacitor';
import { requestAppleIdToken, requestNativeGoogleIdToken, SocialLoginCancelled } from '@/utils/socialProviders';
import type { AppAuthResponse } from '@/types/auth';

interface SocialActions {
  onGoogleCode: (code: string) => void;
  onGoogleNative: () => void;
  onGoogleFail: () => void;
}

function SocialButtonLabel({ icon, label }: { icon: string; label: string }) {
  return (
    <>
      <Icon icon={icon} aria-hidden />
      {label}
    </>
  );
}

function WebGoogleLoginButton({ disabled, onCode, onFail }: { disabled: boolean; onCode: (code: string) => void; onFail: () => void }) {
  const login = useGoogleLogin({
    flow: 'auth-code',
    scope: 'openid email profile',
    ux_mode: 'popup',
    onSuccess: (response) => onCode(response.code),
    onError: () => onFail(),
    onNonOAuthError: (error) => {
      if (error.type === 'popup_closed') return;
      onFail();
    },
  });

  return (
    <Button type="button" variant="outline" className="w-full" disabled={disabled} onClick={() => login()}>
      <SocialButtonLabel icon="logos:google-icon" label="Masuk dengan Google" />
    </Button>
  );
}

function GoogleLoginButton({ disabled, actions }: { disabled: boolean; actions: SocialActions }) {
  const platform = getNativePlatform();
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.trim();
  if (platform === 'web' && clientId) {
    return <WebGoogleLoginButton disabled={disabled} onCode={actions.onGoogleCode} onFail={actions.onGoogleFail} />;
  }
  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      disabled={disabled}
      onClick={() => {
        if (platform === 'web') {
          actions.onGoogleFail();
          return;
        }
        actions.onGoogleNative();
      }}
    >
      <SocialButtonLabel icon="logos:google-icon" label="Masuk dengan Google" />
    </Button>
  );
}

export function SocialLoginButtons({
  disabled = false,
  stayOnCurrentPage = false,
  onSuccess,
}: {
  disabled?: boolean;
  stayOnCurrentPage?: boolean;
  onSuccess?: () => void;
}) {
  const { startSession } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const returnUrl = stayOnCurrentPage
    ? `${location.pathname}${location.search}`
    : searchParams.get('returnUrl');
  const [apiError, setApiError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const platform = getNativePlatform();
  const showApple = platform === 'web' || platform === 'ios';
  const locked = disabled || busy;

  const run = async (action: () => Promise<AppAuthResponse>, fallback: string) => {
    setApiError(null);
    setBusy(true);
    try {
      const data = await action();
      await startSession(data, returnUrl);
      onSuccess?.();
    } catch (error) {
      if (error instanceof SocialLoginCancelled) return;
      setApiError(error instanceof Error && error.message ? error.message : fallback);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">atau</span>
        <span className="h-px flex-1 bg-border" />
      </div>
      {apiError && (
        <Alert variant="destructive">
          <AlertDescription>{apiError}</AlertDescription>
        </Alert>
      )}
      {showApple && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={locked}
          onClick={() => {
            void run(() => requestAppleIdToken().then((token) => authService.loginWithApple(token)), 'Gagal masuk dengan Apple');
          }}
        >
          <SocialButtonLabel icon="simple-icons:apple" label="Masuk dengan Apple" />
        </Button>
      )}
      <GoogleLoginButton
        disabled={locked}
        actions={{
          onGoogleCode: (code) => {
            void run(() => authService.loginWithGoogleWeb(code), 'Gagal masuk dengan Google');
          },
          onGoogleNative: () => {
            void run(() => requestNativeGoogleIdToken().then((token) => authService.loginWithGoogleNative(token)), 'Gagal masuk dengan Google');
          },
          onGoogleFail: () => setApiError('Gagal masuk dengan Google'),
        }}
      />
    </div>
  );
}
